import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import api from './api';
import { useMe } from './Me';
import Markdown from './Markdown';

// What changed since you last looked.
//
// The list is the Done log, which already carries a paragraph per shipped change written to be
// read — so there is nothing to author twice, and nothing to forget to author. The bold opening of
// each entry is the line; the rest is there for anybody who wants to know how it works.
//
// Owner only for now, deliberately. It is the first thing anybody sees after a deploy, and a popup
// that lands wrong should land on one person before it lands on the team.
//
// And a picture, where somebody has added one. The write-up says what a thing does and cannot say
// where it is: "the gap column sorts most underpriced first" is a sentence read twice and then
// gone looking for, while a screenshot of the row answers it at a glance. Pasted or dropped onto
// the entry by whoever read it and thought so, which is the only person who knows.

// Opened on purpose as well as on arrival — from the sidebar, showing everything rather than only
// what is unread. Without that, the only entries anybody could put a picture on would be the ones
// they had not read yet.
let opener = null;
export const openWhatsNew = () => { if (opener) opener(); };

export default function WhatsNew() {
  const me = useMe();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [all, setAll] = useState(false);     // opened deliberately, rather than shown on arrival
  const [adding, setAdding] = useState('');  // the entry a picture is being put on
  const [error, setError] = useState('');

  const isOwner = !!(me && me.owner);

  const load = useCallback(async (everything) => {
    try {
      const r = await api.get('/whats-new', { params: everything ? { all: 1 } : {} });
      setItems((r.data && r.data.items) || []);
      return (r.data && r.data.items) || [];
    } catch { return []; }
  }, []);

  useEffect(() => {
    if (!isOwner) return;
    let alive = true;
    api.get('/whats-new').then(r => { if (alive) setItems((r.data && r.data.items) || []); }).catch(() => {});
    return () => { alive = false; };
  }, [isOwner]);

  // The sidebar's way in. Everything, newest first, with the first one open — somebody who came
  // here on purpose is looking for something rather than being told about it.
  useEffect(() => {
    opener = async () => {
      setAll(true); setError('');
      const list = await load(true);
      setOpen(new Set(list.length ? [0] : []));
    };
    return () => { opener = null; };
  }, [load]);

  // Escape closes it the same as the button — it is a notice, not a decision.
  useEffect(() => {
    if (!items.length) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') dismiss(); };
    document.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; document.removeEventListener('keydown', onKey); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  const dismiss = async () => {
    setBusy(true);
    // Nothing is marked read when it was opened on purpose: somebody looking something up has not
    // been shown the things they have not read yet, and marking them would lose them.
    if (!all) {
      // Closed either way. Failing to record that it was read is not a reason to show it again in
      // the same session — and the worst case is seeing it once more tomorrow.
      try { await api.post('/whats-new/seen'); } catch { /* it will come round again */ }
    }
    setItems([]); setAll(false); setAdding(''); setBusy(false);
  };

  // A picture, from the clipboard or dropped on. It goes through the same image store a task
  // description uses, so there is one place holding an uploaded picture and one URL serving it.
  const addPicture = async (entry, file) => {
    if (!file) return;
    setError('');
    try {
      const data = await new Promise((ok, no) => { const fr = new FileReader(); fr.onload = () => ok(fr.result); fr.onerror = no; fr.readAsDataURL(file); });
      const { url } = (await api.post('/images', { file_name: file.name || 'screenshot.png', file_type: file.type, data })).data;
      await api.post('/whats-new/media', { entry, url });
      const list = await load(all);
      setAdding('');
      // Keep the entry it was added to open, so the picture is visible the moment it lands.
      const i = list.findIndex(x => x.title === entry);
      if (i >= 0) setOpen(s => new Set([...s, i]));
    } catch (e) { setError(e.response?.data?.error || 'Could not add that picture.'); }
  };
  const caption = async (id, text) => {
    try { await api.put(`/whats-new/media/${id}`, { caption: text }); await load(all); }
    catch { setError('Could not save that caption.'); }
  };
  const removePicture = async (id) => {
    try { await api.delete(`/whats-new/media/${id}`); await load(all); }
    catch { setError('Could not remove that.'); }
  };
  const onPaste = (entry) => (e) => {
    for (const it of (e.clipboardData && e.clipboardData.items) || []) {
      if (it.type && it.type.startsWith('image/')) {
        const blob = it.getAsFile();
        if (blob) { e.preventDefault(); addPicture(entry, blob); return; }
      }
    }
  };

  if (!items.length) return null;

  const toggle = (i) => setOpen(s => { const n = new Set(s); if (n.has(i)) n.delete(i); else n.add(i); return n; });

  return createPortal(
    <div className="whatsnew-overlay" onClick={dismiss}>
      <div className="whatsnew" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="What's new">
        <div className="whatsnew-head">
          <span className="whatsnew-title">✨ What’s new</span>
          <span className="whatsnew-count">{all ? `${items.length} update${items.length === 1 ? '' : 's'}` : `${items.length} since you last looked`}</span>
        </div>
        {error && <div className="bankrec-error" style={{ margin: '0 18px' }}>{error}</div>}
        <div className="whatsnew-list">
          {items.map((it, i) => (
            <div className="whatsnew-item" key={it.title}>
              <button className="whatsnew-item-head" onClick={() => toggle(i)} aria-expanded={open.has(i)}>
                <span className="whatsnew-caret">{open.has(i) ? '▾' : '▸'}</span>
                <span className="whatsnew-item-title">{it.title}</span>
                {(it.media || []).length > 0 && <span className="whatsnew-pics" title={`${it.media.length} picture${it.media.length === 1 ? '' : 's'}`}>🖼 {it.media.length}</span>}
              </button>
              {open.has(i) && (
                <>
                  {it.body && <Markdown className="whatsnew-body" text={it.body} />}
                  {(it.media || []).length > 0 && (
                    <div className="whatsnew-media">
                      {it.media.map(m => (
                        <figure key={m.id}>
                          <img src={m.url} alt={m.caption || it.title} />
                          {isOwner
                            ? (
                              <figcaption>
                                <input className="form-input" placeholder="Say what this shows (optional)"
                                  defaultValue={m.caption || ''}
                                  onBlur={e => { if ((m.caption || '') !== e.target.value) caption(m.id, e.target.value); }}
                                  onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }} />
                                <button className="link-btn danger" onClick={() => removePicture(m.id)}>remove</button>
                              </figcaption>
                            )
                            : m.caption ? <figcaption>{m.caption}</figcaption> : null}
                        </figure>
                      ))}
                    </div>
                  )}
                  {isOwner && (
                    adding === it.title ? (
                      <label className={`whatsnew-drop`}
                        onPaste={onPaste(it.title)}
                        onDragOver={e => e.preventDefault()}
                        onDrop={e => { e.preventDefault(); addPicture(it.title, e.dataTransfer.files && e.dataTransfer.files[0]); }}>
                        <input type="file" accept="image/*" style={{ display: 'none' }}
                          onChange={e => { addPicture(it.title, e.target.files[0]); e.target.value = ''; }} />
                        {/* Focusable, so a paste lands here and not on the page behind it. */}
                        <input className="whatsnew-paste" readOnly value="" autoFocus aria-label="Paste a screenshot" onPaste={onPaste(it.title)} />
                        Paste a screenshot, drop one in, or click to choose a file.
                      </label>
                    ) : (
                      <button className="link-btn" onClick={() => setAdding(it.title)}>+ Add a picture</button>
                    )
                  )}
                </>
              )}
            </div>
          ))}
        </div>
        <div className="whatsnew-foot">
          <button className="btn btn-primary" disabled={busy} onClick={dismiss}>{all ? 'Close' : 'Got it'}</button>
          {!all && (
            <button className="link-btn" onClick={() => openWhatsNew()}>See everything, including what you have read</button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
